import { useMDSContext } from '@metservice/use-mds';
import {
    getUnitMarkupFromCapabilityVariable,
    getOpinionatedBestUnitDefinition,
    getAlternateUnitsForUnit,
    getOrMakeUnitDefinitionFromMdsSiMarkup,
} from '@metservicedev/cf-units';
import { Input, Select } from 'antd';
import React, { useEffect } from 'react';

import { fuzzySearch } from '@mono/fuzzy-search';

const FALLBACK_UNIT = 'dimensionless';

interface UnitSelectorProps {
    onChange(unit: string): void;
    value?: string;
    capabilityId?: string;
    unitLabel?: string;
    onUnitLabelChange?(label: string): void;
}

const UnitSelector: React.FC<UnitSelectorProps> = ({ onChange, value: currentDisplayUnit, capabilityId, unitLabel, onUnitLabelChange }) => {
    const { capabilities } = useMDSContext();
    const wholeCapability = capabilities.find(({ id }) => id === capabilityId);

    const siUnit = wholeCapability ? getUnitMarkupFromCapabilityVariable(wholeCapability) : FALLBACK_UNIT;
    const siUnitDefinition = getOpinionatedBestUnitDefinition(siUnit || '');
    const alternateUnitDefinitions = getAlternateUnitsForUnit(siUnitDefinition.unit).map((u) => getOrMakeUnitDefinitionFromMdsSiMarkup(u));
    const isUnitValidOption = alternateUnitDefinitions.findIndex((unitDefinition) => unitDefinition.unit === currentDisplayUnit) >= 0;

    // If the siUnit changes or whether the unit is valid changes, if not valid unit, change to the siUnit.
    useEffect(() => {
        if (!isUnitValidOption) {
            console.warn(`As ${currentDisplayUnit} is not a valid alternate unit for ${siUnitDefinition.unit}, swapping to ${siUnitDefinition.unit}`);
            onChange(siUnitDefinition.unit);
        }
    }, [isUnitValidOption, siUnitDefinition.unit]);

    if (!wholeCapability) {
        return <Select style={{ width: '100%' }} disabled placeholder={'no capability'} />;
    }

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
            <Input.Group compact>
                <Select
                    showSearch={true}
                    optionFilterProp={'children'}
                    style={{ width: onUnitLabelChange ? '50%' : '100%' }}
                    filterOption={(input, option) => fuzzySearch(input, [option?.props.children?.toString() || ''])}
                    onSelect={onChange}
                    value={isUnitValidOption ? currentDisplayUnit : siUnitDefinition.unit}
                    dropdownMatchSelectWidth={false}
                >
                    {alternateUnitDefinitions.map((unit) => {
                        return (
                            <Select.Option key={unit.unit} value={unit.unit}>
                                {unit.name}
                            </Select.Option>
                        );
                    })}
                </Select>
                {onUnitLabelChange && (
                    <Input
                        onChange={(e) => {
                            onUnitLabelChange(e.target.value);
                        }}
                        style={{ width: '50%' }}
                        value={unitLabel}
                    />
                )}
            </Input.Group>
        </div>
    );
};

export default UnitSelector;
